import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useMsal } from "@azure/msal-react";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Cell,
} from "recharts";
import { sharepointScopes } from "../auth/msalConfig";
import { fetchCpcaCases, CpcaCase } from "../api/sharepointCpca";

// ── helpers ──────────────────────────────────────────────────────────────────

function daysBetween(a: string | null, b: string | null): number | null {
  if (!a || !b) return null;
  const diff = new Date(b).getTime() - new Date(a).getTime();
  return Math.round(diff / 86_400_000);
}

function avg(nums: number[]): number {
  if (!nums.length) return 0;
  return Math.round(nums.reduce((s, n) => s + n, 0) / nums.length);
}

function fmtDate(d: string | null) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("es-PR");
}

function fmtDays(n: number | null) {
  if (n === null || n < 0) return "—";
  return `${n}d`;
}

// ── sub-types ─────────────────────────────────────────────────────────────────

interface EvalRow {
  name: string;
  total: number;
  completed: number;
  inProgress: number;
  rejected: number;
  avgAssignToUpload: number | null;
  avgUploadToActa: number | null;
}

interface TasadorRow {
  name: string;
  total: number;
  completed: number;
  inProgress: number;
  rejected: number;
  avgDays: number | null;
}

interface MonthPoint {
  month: string;
  completed: number;
  avgCycle: number;
}

type SortDir = "asc" | "desc";

// ── SortableHeader ────────────────────────────────────────────────────────────

function SortableHeader({
  label, field, sortField, sortDir, onSort,
}: {
  label: string;
  field: string;
  sortField: string;
  sortDir: SortDir;
  onSort: (f: string) => void;
}) {
  const active = sortField === field;
  return (
    <th
      className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase cursor-pointer select-none hover:text-gray-700 whitespace-nowrap"
      onClick={() => onSort(field)}
    >
      {label}
      {active && (
        <span className="ml-1 text-blue-500">{sortDir === "asc" ? "▲" : "▼"}</span>
      )}
    </th>
  );
}

// ── Skeleton card ─────────────────────────────────────────────────────────────

function SkeletonCard() {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 animate-pulse">
      <div className="h-3 bg-gray-200 rounded w-24 mb-3" />
      <div className="h-7 bg-gray-200 rounded w-16 mb-2" />
      <div className="h-2 bg-gray-100 rounded w-20" />
    </div>
  );
}

// ── Estado badge ──────────────────────────────────────────────────────────────

// "Rechazado" is displayed to users as "En Revisión" (business wording change);
// the underlying data value and its red badge color are unchanged for internal logic.
function displayEstadoLabel(estado: string): string {
  return estado === "Rechazado" ? "En Revisión" : estado;
}

function EstadoBadge({ estado }: { estado: string }) {
  const cls: Record<string, string> = {
    "ODAP": "bg-gray-100 text-gray-600",
    "Pendiente": "bg-blue-100 text-blue-700",
    "Asignado": "bg-blue-100 text-blue-700",
    "En Revisión": "bg-amber-100 text-amber-700",
    "Rechazado": "bg-red-100 text-red-700",
    "Aprobado": "bg-emerald-100 text-emerald-700",
    "Acta Completada": "bg-green-100 text-green-800 font-semibold",
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs ${cls[estado] ?? "bg-gray-50 text-gray-500"}`}>
      {displayEstadoLabel(estado) || "—"}
    </span>
  );
}

// ── PIPELINE BAR ──────────────────────────────────────────────────────────────

const PIPELINE_STAGES = ["ODAP", "Asignado", "En Revisión", "Aprobado", "Acta Completada"] as const;
const STAGE_COLORS = ["#6b7280", "#3b82f6", "#f59e0b", "#10b981", "#059669"];

// ── Main component ────────────────────────────────────────────────────────────

export const CPCADashboard: React.FC = () => {
  const { instance } = useMsal();
  const [cases, setCases] = useState<CpcaCase[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  // table sort state
  const [evalSort, setEvalSort] = useState<{ field: string; dir: SortDir }>({ field: "total", dir: "desc" });
  const [tasSort, setTasSort] = useState<{ field: string; dir: SortDir }>({ field: "total", dir: "desc" });
  const [recentSort, setRecentSort] = useState<{ field: string; dir: SortDir }>({ field: "modified", dir: "desc" });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let tokenResponse;
      try {
        tokenResponse = await instance.acquireTokenSilent({
          scopes: sharepointScopes,
          account: instance.getActiveAccount() || undefined,
        });
      }  catch (silentErr) {
        const code = (silentErr as { errorCode?: string }).errorCode ?? "";
        if (
          silentErr instanceof InteractionRequiredAuthError ||
          code === "timed_out" ||
          code === "monitor_window_timeout"
        ) {
          await instance.acquireTokenRedirect({ scopes: sharepointScopes });
          return;
        }
        throw silentErr;
      }

      const data = await fetchCpcaCases(tokenResponse.accessToken);
      setCases(data);
      setLastUpdated(new Date());
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [instance]);

  useEffect(() => { load(); }, [load]);

  // ── derived metrics ─────────────────────────────────────────────────────────

  const summary = useMemo(() => ({
    total: cases.length,
    pendingAssignment: cases.filter(c => c.estado === "ODAP").length,
    inProgress: cases.filter(c => ["Asignado", "En Revisión", "Rechazado"].includes(c.estado)).length,
    completed: cases.filter(c => c.estado === "Acta Completada").length,
    tasadorPending: cases.filter(c => c.estadoTasador === "Pendiente").length,
    tasadorComplete: cases.filter(c => c.estadoTasador === "Aprobado").length,
  }), [cases]);

  const pipelineCounts = useMemo(() =>
    PIPELINE_STAGES.map((stage, i) => ({
      stage,
      count: cases.filter(c => c.estado === stage).length,
      color: STAGE_COLORS[i],
    })),
    [cases]
  );

  const evalRows: EvalRow[] = useMemo(() => {
    const map = new Map<string, CpcaCase[]>();
    cases.forEach(c => {
      if (!c.asignadoA) return;
      if (!map.has(c.asignadoA)) map.set(c.asignadoA, []);
      map.get(c.asignadoA)!.push(c);
    });
    return Array.from(map.entries()).map(([name, cs]) => {
      const a2u = cs.map(c => daysBetween(c.fechaAsignacion, c.fechaSubida)).filter((n): n is number => n !== null && n >= 0);
      const u2a = cs.map(c => daysBetween(c.fechaSubida, c.fechaActa)).filter((n): n is number => n !== null && n >= 0);
      return {
        name,
        total: cs.length,
        completed: cs.filter(c => c.estado === "Acta Completada").length,
        inProgress: cs.filter(c => ["Asignado", "En Revisión"].includes(c.estado)).length,
        rejected: cs.filter(c => c.estado === "Rechazado").length,
        avgAssignToUpload: a2u.length ? avg(a2u) : null,
        avgUploadToActa: u2a.length ? avg(u2a) : null,
      };
    });
  }, [cases]);

  const sortedEvalRows = useMemo(() => {
    const rows = [...evalRows];
    rows.sort((a, b) => {
      const av = (a as unknown as Record<string, unknown>)[evalSort.field] ?? 0;
      const bv = (b as unknown as Record<string, unknown>)[evalSort.field] ?? 0;
      return evalSort.dir === "asc"
        ? (av as number) - (bv as number)
        : (bv as number) - (av as number);
    });
    return rows;
  }, [evalRows, evalSort]);

  const tasadorRows: TasadorRow[] = useMemo(() => {
    const map = new Map<string, CpcaCase[]>();
    cases.forEach(c => {
      if (!c.tasador) return;
      if (!map.has(c.tasador)) map.set(c.tasador, []);
      map.get(c.tasador)!.push(c);
    });
    return Array.from(map.entries()).map(([name, cs]) => {
      const days = cs
        .map(c => daysBetween(c.fechaAsignacionTasador, c.fechaSubidaTasador))
        .filter((n): n is number => n !== null && n >= 0);
      return {
        name,
        total: cs.length,
        completed: cs.filter(c => c.estadoTasador === "Aprobado").length,
        inProgress: cs.filter(c => ["Pendiente", "En Revisión"].includes(c.estadoTasador)).length,
        rejected: cs.filter(c => c.estadoTasador === "Rechazado").length,
        avgDays: days.length ? avg(days) : null,
      };
    });
  }, [cases]);

  const sortedTasadorRows = useMemo(() => {
    const rows = [...tasadorRows];
    rows.sort((a, b) => {
      const av = (a as unknown as Record<string, unknown>)[tasSort.field] ?? 0;
      const bv = (b as unknown as Record<string, unknown>)[tasSort.field] ?? 0;
      return tasSort.dir === "asc"
        ? (av as number) - (bv as number)
        : (bv as number) - (av as number);
    });
    return rows;
  }, [tasadorRows, tasSort]);

  const managerMetrics = useMemo(() => {
    const approved = cases.filter(c => c.estado === "Aprobado" || c.estado === "Acta Completada").length;
    const evalRejections = cases.filter(c => c.estado === "Rechazado").length;
    const tasRejections = cases.filter(c => c.estadoTasador === "Rechazado").length;
    const awaitingReview = cases.filter(c => c.estado === "En Revisión").length;
    const cycleDays = cases
      .filter(c => c.fechaAsignacion && c.fechaActa)
      .map(c => daysBetween(c.fechaAsignacion, c.fechaActa))
      .filter((n): n is number => n !== null && n >= 0);
    return {
      approved,
      totalRejections: evalRejections + tasRejections,
      evalRejections,
      tasRejections,
      avgCycleDays: cycleDays.length ? avg(cycleDays) : null,
      awaitingReview,
    };
  }, [cases]);

  const monthlyData: MonthPoint[] = useMemo(() => {
    const map = new Map<string, { completed: number; cycles: number[] }>();
    cases
      .filter(c => c.estado === "Acta Completada" && c.fechaActa)
      .forEach(c => {
        const d = new Date(c.fechaActa!);
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
        if (!map.has(key)) map.set(key, { completed: 0, cycles: [] });
        const entry = map.get(key)!;
        entry.completed++;
        const cyc = daysBetween(c.fechaAsignacion, c.fechaActa);
        if (cyc !== null && cyc >= 0) entry.cycles.push(cyc);
      });
    return Array.from(map.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .slice(-12)
      .map(([month, v]) => ({
        month,
        completed: v.completed,
        avgCycle: v.cycles.length ? avg(v.cycles) : 0,
      }));
  }, [cases]);

  // Cases where an evaluator or tasador is actively working (not yet completed or unassigned)
  const activeCases = useMemo(() => {
    return cases
      .filter(c =>
        (c.asignadoA !== "" && c.estado !== "Acta Completada") || ["Pendiente", "En Revisión"].includes(c.estadoTasador)
      )
      .sort((a, b) =>
        new Date(b.modified ?? 0).getTime() - new Date(a.modified ?? 0).getTime()
      );
  }, [cases]);

  const sortedActiveCases = useMemo(() => {
    const rows = [...activeCases];
    rows.sort((a, b) => {
      const f = recentSort.field as keyof CpcaCase;
      const av = String(a[f] ?? "");
      const bv = String(b[f] ?? "");
      return recentSort.dir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
    });
    return rows;
  }, [activeCases, recentSort]);

  const rejectionAnalysis = useMemo(() => {
    const evalRej = new Map<string, number>();
    const tasRej = new Map<string, number>();
    const evalComments: string[] = [];
    const tasComments: string[] = [];

    cases.forEach(c => {
      if (c.estado === "Rechazado" && c.asignadoA) {
        evalRej.set(c.asignadoA, (evalRej.get(c.asignadoA) ?? 0) + 1);
        if (c.comentariosRechazo) evalComments.push(c.comentariosRechazo);
      }
      if (c.estadoTasador === "Rechazado" && c.tasador) {
        tasRej.set(c.tasador, (tasRej.get(c.tasador) ?? 0) + 1);
        if (c.comentariosRechazoTasador) tasComments.push(c.comentariosRechazoTasador);
      }
    });

    return {
      byEvaluator: Array.from(evalRej.entries()).sort((a, b) => b[1] - a[1]),
      byTasador: Array.from(tasRej.entries()).sort((a, b) => b[1] - a[1]),
      evalComments: evalComments.slice(0, 5),
      tasComments: tasComments.slice(0, 5),
    };
  }, [cases]);

  // ── sort handlers ───────────────────────────────────────────────────────────

  const handleEvalSort = (field: string) => {
    setEvalSort(prev =>
      prev.field === field ? { field, dir: prev.dir === "asc" ? "desc" : "asc" } : { field, dir: "desc" }
    );
  };
  const handleTasSort = (field: string) => {
    setTasSort(prev =>
      prev.field === field ? { field, dir: prev.dir === "asc" ? "desc" : "asc" } : { field, dir: "desc" }
    );
  };
  const handleRecentSort = (field: string) => {
    setRecentSort(prev =>
      prev.field === field ? { field, dir: prev.dir === "asc" ? "desc" : "asc" } : { field, dir: "desc" }
    );
  };

  // ── render ──────────────────────────────────────────────────────────────────

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <div className="text-red-600 font-medium mb-1">Error cargando datos CPCA</div>
          <div className="text-red-500 text-sm mb-4">{error}</div>
          <button
            onClick={load}
            className="px-4 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 transition"
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">

      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Dashboard CPCA — Comité de Arrendamiento</h2>
          <p className="text-sm text-gray-500 mt-0.5">Seguimiento del proceso de evaluación de casos</p>
          {lastUpdated && (
            <p className="text-xs text-gray-400 mt-0.5">
              Actualizado: {lastUpdated.toLocaleString("es-PR")}
            </p>
          )}
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="px-4 py-2 bg-brand-800 text-white text-sm font-medium rounded-lg hover:bg-brand-700 transition disabled:opacity-50 flex items-center gap-2"
        >
          <svg className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Actualizar
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        {loading ? (
          Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)
        ) : (
          <>
            <SummaryCard label="Total Casos" value={summary.total} color="border-gray-400" sub="en la lista" />
            <SummaryCard label="Sin Asignar" value={summary.pendingAssignment} color="border-gray-500" sub="Estado: ODAP" />
            <SummaryCard label="En Proceso" value={summary.inProgress} color="border-blue-500" sub="Asignado / En Revisión" />
            <SummaryCard label="Completados" value={summary.completed} color="border-green-500" sub="Acta Completada" />
            <SummaryCard label="Tasación Pend." value={summary.tasadorPending} color="border-amber-500" sub="Est. Tasador: Pendiente" />
            <SummaryCard label="Tasación OK" value={summary.tasadorComplete} color="border-emerald-500" sub="Est. Tasador: Aprobado" />
          </>
        )}
      </div>

      {/* Pipeline + Manager Metrics row */}
      <div className="grid lg:grid-cols-2 gap-6">

        {/* Pipeline */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">Pipeline de Estados</h3>
          {loading ? (
            <div className="h-40 bg-gray-100 rounded animate-pulse" />
          ) : (
            <>
              <ResponsiveContainer width="100%" height={160}>
                <BarChart data={pipelineCounts} layout="vertical" margin={{ left: 10, right: 30 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11 }} />
                  <YAxis dataKey="stage" type="category" tick={{ fontSize: 11 }} width={110} />
                  <Tooltip formatter={(v) => [`${v} casos`, ""]} />
                  <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                    {pipelineCounts.map((entry, i) => (
                      <Cell key={i} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              <div className="flex flex-wrap gap-2 mt-3">
                {pipelineCounts.map((s, i) => (
                  <span key={s.stage} className="flex items-center gap-1 text-xs text-gray-600">
                    <span className="w-2.5 h-2.5 rounded-sm inline-block" style={{ background: STAGE_COLORS[i] }} />
                    {s.stage}: <strong>{s.count}</strong>
                  </span>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Manager Metrics */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">Métricas Gerenciales</h3>
          {loading ? (
            <div className="space-y-3">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="h-12 bg-gray-100 rounded animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4">
              <MetricTile label="Aprobados por Gerencia" value={managerMetrics.approved} />
              <MetricTile label="En Revisión (Totales)" value={managerMetrics.totalRejections} sub={`${managerMetrics.evalRejections} evaluador · ${managerMetrics.tasRejections} tasador`} />
              <MetricTile
                label="Tiempo Ciclo Promedio"
                value={managerMetrics.avgCycleDays !== null ? `${managerMetrics.avgCycleDays}d` : "—"}
                sub="Asignación → Acta"
              />
              <MetricTile label="En Revisión Gerencial" value={managerMetrics.awaitingReview} />
            </div>
          )}
        </div>
      </div>

      {/* Time Metrics Chart */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
        <h3 className="text-sm font-semibold text-gray-700 mb-4">Tendencia Mensual (últimos 12 meses)</h3>
        {loading ? (
          <div className="h-48 bg-gray-100 rounded animate-pulse" />
        ) : monthlyData.length === 0 ? (
          <EmptyState message="No hay casos completados con fechas registradas." />
        ) : (
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={monthlyData} margin={{ left: 0, right: 20 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis yAxisId="left" tick={{ fontSize: 11 }} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} />
              <Tooltip />
              <Line yAxisId="left" type="monotone" dataKey="completed" stroke="#3b82f6" strokeWidth={2} dot name="Casos Completados" />
              <Line yAxisId="right" type="monotone" dataKey="avgCycle" stroke="#f59e0b" strokeWidth={2} dot name="Días Promedio Ciclo" strokeDasharray="4 2" />
            </LineChart>
          </ResponsiveContainer>
        )}
        <div className="flex gap-6 mt-2">
          <span className="flex items-center gap-1.5 text-xs text-gray-500">
            <span className="w-4 h-0.5 bg-blue-500 inline-block" /> Casos completados
          </span>
          <span className="flex items-center gap-1.5 text-xs text-gray-500">
            <span className="w-4 h-0.5 bg-amber-500 inline-block border-dashed" /> Días promedio ciclo
          </span>
        </div>
      </div>

      {/* By Evaluator */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100">
          <h3 className="text-sm font-semibold text-gray-700">Por Evaluador</h3>
        </div>
        {loading ? (
          <TableSkeleton cols={7} rows={4} />
        ) : evalRows.length === 0 ? (
          <EmptyState message="No hay evaluadores asignados." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <SortableHeader label="Evaluador" field="name" sortField={evalSort.field} sortDir={evalSort.dir} onSort={handleEvalSort} />
                  <SortableHeader label="Total" field="total" sortField={evalSort.field} sortDir={evalSort.dir} onSort={handleEvalSort} />
                  <SortableHeader label="Completados" field="completed" sortField={evalSort.field} sortDir={evalSort.dir} onSort={handleEvalSort} />
                  <SortableHeader label="En Proceso" field="inProgress" sortField={evalSort.field} sortDir={evalSort.dir} onSort={handleEvalSort} />
                  <SortableHeader label="En Revisión" field="rejected" sortField={evalSort.field} sortDir={evalSort.dir} onSort={handleEvalSort} />
                  <SortableHeader label="Prom. Asig→Subida" field="avgAssignToUpload" sortField={evalSort.field} sortDir={evalSort.dir} onSort={handleEvalSort} />
                  <SortableHeader label="Prom. Subida→Acta" field="avgUploadToActa" sortField={evalSort.field} sortDir={evalSort.dir} onSort={handleEvalSort} />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {sortedEvalRows.map(r => (
                  <tr key={r.name} className="hover:bg-gray-50">
                    <td className="px-3 py-2.5 font-medium text-gray-900">{r.name}</td>
                    <td className="px-3 py-2.5 text-gray-700">{r.total}</td>
                    <td className="px-3 py-2.5 text-green-700 font-medium">{r.completed}</td>
                    <td className="px-3 py-2.5 text-blue-700">{r.inProgress}</td>
                    <td className="px-3 py-2.5 text-red-600">{r.rejected}</td>
                    <td className="px-3 py-2.5 text-gray-500">{fmtDays(r.avgAssignToUpload)}</td>
                    <td className="px-3 py-2.5 text-gray-500">{fmtDays(r.avgUploadToActa)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* By Tasador */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100">
          <h3 className="text-sm font-semibold text-gray-700">Por Tasador</h3>
        </div>
        {loading ? (
          <TableSkeleton cols={6} rows={4} />
        ) : tasadorRows.length === 0 ? (
          <EmptyState message="No hay tasadores asignados." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <SortableHeader label="Tasador" field="name" sortField={tasSort.field} sortDir={tasSort.dir} onSort={handleTasSort} />
                  <SortableHeader label="Total" field="total" sortField={tasSort.field} sortDir={tasSort.dir} onSort={handleTasSort} />
                  <SortableHeader label="Aprobados" field="completed" sortField={tasSort.field} sortDir={tasSort.dir} onSort={handleTasSort} />
                  <SortableHeader label="En Proceso" field="inProgress" sortField={tasSort.field} sortDir={tasSort.dir} onSort={handleTasSort} />
                  <SortableHeader label="En Revisión" field="rejected" sortField={tasSort.field} sortDir={tasSort.dir} onSort={handleTasSort} />
                  <SortableHeader label="Prom. Asig→Subida" field="avgDays" sortField={tasSort.field} sortDir={tasSort.dir} onSort={handleTasSort} />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {sortedTasadorRows.map(r => (
                  <tr key={r.name} className="hover:bg-gray-50">
                    <td className="px-3 py-2.5 font-medium text-gray-900">{r.name}</td>
                    <td className="px-3 py-2.5 text-gray-700">{r.total}</td>
                    <td className="px-3 py-2.5 text-green-700 font-medium">{r.completed}</td>
                    <td className="px-3 py-2.5 text-blue-700">{r.inProgress}</td>
                    <td className="px-3 py-2.5 text-red-600">{r.rejected}</td>
                    <td className="px-3 py-2.5 text-gray-500">{fmtDays(r.avgDays)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Rejection Analysis */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">En Revisión por Evaluador</h3>
          {loading ? (
            <div className="h-24 bg-gray-100 rounded animate-pulse" />
          ) : rejectionAnalysis.byEvaluator.length === 0 ? (
            <EmptyState message="Sin casos en revisión registrados." />
          ) : (
            <div className="space-y-2">
              {rejectionAnalysis.byEvaluator.map(([name, count]) => (
                <div key={name} className="flex items-center justify-between">
                  <span className="text-sm text-gray-700 truncate flex-1">{name}</span>
                  <span className="ml-2 px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-xs font-semibold">{count}</span>
                </div>
              ))}
              {rejectionAnalysis.evalComments.length > 0 && (
                <div className="mt-4 border-t border-gray-100 pt-3">
                  <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Últimas razones de revisión</p>
                  {rejectionAnalysis.evalComments.map((c, i) => (
                    <p key={i} className="text-xs text-gray-500 italic mb-1 truncate">"{c}"</p>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">En Revisión por Tasador</h3>
          {loading ? (
            <div className="h-24 bg-gray-100 rounded animate-pulse" />
          ) : rejectionAnalysis.byTasador.length === 0 ? (
            <EmptyState message="Sin casos en revisión registrados." />
          ) : (
            <div className="space-y-2">
              {rejectionAnalysis.byTasador.map(([name, count]) => (
                <div key={name} className="flex items-center justify-between">
                  <span className="text-sm text-gray-700 truncate flex-1">{name}</span>
                  <span className="ml-2 px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-xs font-semibold">{count}</span>
                </div>
              ))}
              {rejectionAnalysis.tasComments.length > 0 && (
                <div className="mt-4 border-t border-gray-100 pt-3">
                  <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Últimas razones de revisión</p>
                  {rejectionAnalysis.tasComments.map((c, i) => (
                    <p key={i} className="text-xs text-gray-500 italic mb-1 truncate">"{c}"</p>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Active Cases */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-gray-700">Casos en Proceso</h3>
            <p className="text-xs text-gray-400 mt-0.5">Casos que evaluadores y tasadores tienen actualmente en trabajo</p>
          </div>
          {!loading && (
            <span className="px-2.5 py-1 bg-blue-50 text-blue-700 text-xs font-semibold rounded-full">
              {activeCases.length} casos
            </span>
          )}
        </div>
        {loading ? (
          <TableSkeleton cols={7} rows={5} />
        ) : activeCases.length === 0 ? (
          <EmptyState message="No hay casos activos en este momento." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <SortableHeader label="# Solicitud" field="numeroSolicitud" sortField={recentSort.field} sortDir={recentSort.dir} onSort={handleRecentSort} />
                  <SortableHeader label="Solicitante" field="nombreSolicitante" sortField={recentSort.field} sortDir={recentSort.dir} onSort={handleRecentSort} />
                  <SortableHeader label="Evaluador" field="asignadoA" sortField={recentSort.field} sortDir={recentSort.dir} onSort={handleRecentSort} />
                  <SortableHeader label="Estado" field="estado" sortField={recentSort.field} sortDir={recentSort.dir} onSort={handleRecentSort} />
                  <SortableHeader label="Tasador" field="tasador" sortField={recentSort.field} sortDir={recentSort.dir} onSort={handleRecentSort} />
                  <SortableHeader label="Estado Tasación" field="estadoTasador" sortField={recentSort.field} sortDir={recentSort.dir} onSort={handleRecentSort} />
                  <SortableHeader label="Modificado" field="modified" sortField={recentSort.field} sortDir={recentSort.dir} onSort={handleRecentSort} />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {sortedActiveCases.map(c => (
                  <tr key={c.id} className="hover:bg-gray-50">
                    <td className="px-3 py-2.5 font-mono text-xs text-gray-700">{c.numeroSolicitud || "—"}</td>
                    <td className="px-3 py-2.5 text-gray-900 max-w-[160px] truncate">{c.nombreSolicitante || "—"}</td>
                    <td className="px-3 py-2.5 text-gray-600 max-w-[120px] truncate">{c.asignadoA || "—"}</td>
                    <td className="px-3 py-2.5"><EstadoBadge estado={c.estado} /></td>
                    <td className="px-3 py-2.5 text-gray-600 max-w-[120px] truncate">{c.tasador || "—"}</td>
                    <td className="px-3 py-2.5"><EstadoBadge estado={c.estadoTasador} /></td>
                    <td className="px-3 py-2.5 text-gray-500 whitespace-nowrap">{fmtDate(c.modified)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

    </div>
  );
};

// ── Small helper components ───────────────────────────────────────────────────

function SummaryCard({ label, value, color, sub }: { label: string; value: number; color: string; sub?: string }) {
  return (
    <div className={`bg-white rounded-xl border-l-4 ${color} border border-gray-200 p-5`}>
      <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">{label}</div>
      <div className="text-2xl font-bold text-gray-900">{value}</div>
      {sub && <div className="text-xs text-gray-400 mt-0.5 truncate">{sub}</div>}
    </div>
  );
}

function MetricTile({ label, value, sub }: { label: string; value: string | number; sub?: string }) {
  return (
    <div className="bg-gray-50 rounded-lg p-4">
      <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">{label}</div>
      <div className="text-xl font-bold text-gray-900">{value}</div>
      {sub && <div className="text-xs text-gray-400 mt-0.5">{sub}</div>}
    </div>
  );
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="px-6 py-10 text-center text-gray-400 text-sm">{message}</div>
  );
}

function TableSkeleton({ cols, rows }: { cols: number; rows: number }) {
  return (
    <div className="p-4 space-y-2">
      {Array.from({ length: rows }).map((_, r) => (
        <div key={r} className="flex gap-3 animate-pulse">
          {Array.from({ length: cols }).map((_, c) => (
            <div key={c} className="h-6 bg-gray-100 rounded flex-1" />
          ))}
        </div>
      ))}
    </div>
  );
}
